import { useEffect, useState } from "react";
import isEqual from "lodash/isEqual";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  DeleteOutlined,
  DragOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Input } from "antd";
import { VivoPlotProps } from "@/components/VivoPlots/types";
import type { DashboardEditorProps } from "../tableToolbar.types";
import { setAllHidden, updatePlot } from "./dashboardEditorUtils";

export const DashboardEditor = ({
  plots = [],
  setPlots,
  onClose,
  onAddNewWidget,
  onEditPlot,
  onEditLayout,
}: DashboardEditorProps) => {
  const [editedPlots, setEditedPlots] = useState<VivoPlotProps[]>(plots);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => setEditedPlots(plots), [plots]);

  const shownPlots = editedPlots.filter((plot) => !plot.config.hidden);
  const hiddenPlots = editedPlots.filter((plot) => plot.config.hidden);
  const query = searchQuery.trim().toLowerCase();
  const filteredShownPlots = query
    ? shownPlots.filter((plot) =>
        (plot.config.labels?.title || "").toLowerCase().includes(query),
      )
    : shownPlots;
  const hasChanges = !isEqual(editedPlots, plots);

  const applyChanges = () => {
    setPlots(editedPlots);
    onClose();
  };

  return (
    <div className="flex h-[480px] w-[580px] flex-col max-w-[calc(100vw-32px)]">
      <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] px-3 py-2">
        <button
          type="button"
          data-testid="create-new-plot-button"
          onClick={onAddNewWidget}
          className="flex items-center gap-1 text-sm font-medium text-editor-link-deep hover:opacity-80"
        >
          <PlusOutlined className="text-xs" /> Add new widget
        </button>
        <button
          type="button"
          data-testid="plot-editor-edit-layout-button"
          onClick={() => {
            setPlots(editedPlots);
            onClose();
            onEditLayout(editedPlots);
          }}
          disabled={editedPlots.length === 0}
          className="flex items-center gap-1 text-sm font-medium text-editor-link hover:opacity-80 disabled:cursor-default disabled:opacity-40"
          aria-label="Edit layout"
        >
          <DragOutlined className="text-xs" /> Edit layout
        </button>
      </div>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <section
          className="w-[250px] overflow-y-auto p-4"
          aria-label="Hidden widgets"
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-medium text-brand-dark">Hidden</h3>
            <button
              type="button"
              data-testid="plot-editor-show-all-button"
              onClick={() =>
                setEditedPlots((items) => setAllHidden(items, false))
              }
              className="flex items-center gap-1 text-sm font-medium text-editor-link hover:opacity-80"
            >
              Show all <ArrowRightOutlined className="text-xs" />
            </button>
          </div>
          <div
            data-testid="plot-editor-hidden-section"
            className="flex flex-col gap-2"
          >
            {hiddenPlots.map((plot) => (
              <button
                type="button"
                data-testid={`plot-editor-hidden-plot-${plot.config.plotId}`}
                key={plot.config.plotId}
                onClick={() =>
                  setEditedPlots((items) =>
                    updatePlot(items, plot.config.plotId, { hidden: false }),
                  )
                }
                className="flex h-9 items-center gap-2 rounded-lg bg-black/[0.03] px-2 text-left hover:bg-black/[0.06]"
                title="Show widget"
              >
                <BarChartOutlined className="shrink-0 text-base text-editor-muted" />
                <span className="truncate text-sm text-black/85">
                  {plot.config.labels?.title || "Untitled"}
                </span>
              </button>
            ))}
            {hiddenPlots.length === 0 && (
              <p className="text-sm italic text-editor-muted">
                No hidden widgets
              </p>
            )}
          </div>
        </section>

        <div className="w-px self-stretch bg-black/10" />

        <section
          className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
          aria-label="Shown widgets"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium text-brand-dark">Shown</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="plot-editor-hide-all-button"
                onClick={() =>
                  setEditedPlots((items) => setAllHidden(items, true))
                }
                className="flex items-center gap-1 text-sm font-medium text-editor-link hover:opacity-80"
              >
                <ArrowLeftOutlined className="text-xs" /> Hide all
              </button>
              <button
                type="button"
                onClick={() => setEditedPlots(hiddenPlots)}
                className="rounded bg-black/[0.04] px-2 py-0.5 text-sm font-medium text-editor-icon hover:bg-black/[0.08]"
              >
                Delete shown
              </button>
            </div>
          </div>

          <Input
            prefix={<SearchOutlined className="text-black/45" />}
            placeholder="Search widgets"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            allowClear
          />

          <div
            data-testid="plot-editor-shown-section"
            className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
          >
            {filteredShownPlots.map((plot) => (
              <div
                data-testid={`plot-editor-shown-plot-${plot.config.plotId}`}
                key={plot.config.plotId}
                className="group flex h-9 items-center justify-between rounded-lg bg-black/[0.03] px-2"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <BarChartOutlined className="shrink-0 text-base text-editor-muted" />
                  <span className="truncate text-sm text-black/85">
                    {plot.config.labels?.title || "Untitled"}
                  </span>
                </div>
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                  <button
                    type="button"
                    data-testid={`plot-editor-edit-button-${plot.config.plotId}`}
                    onClick={() => onEditPlot(plot)}
                    className="grid size-6 place-items-center text-editor-icon hover:text-brand-dark"
                    aria-label={`Edit ${plot.config.labels?.title || "widget"}`}
                  >
                    <EditOutlined />
                  </button>
                  <button
                    type="button"
                    data-testid={`plot-editor-hide-button-${plot.config.plotId}`}
                    onClick={() =>
                      setEditedPlots((items) =>
                        updatePlot(items, plot.config.plotId, { hidden: true }),
                      )
                    }
                    className="grid size-6 place-items-center text-editor-icon hover:text-brand-dark"
                    aria-label={`Hide ${plot.config.labels?.title || "widget"}`}
                  >
                    <EyeInvisibleOutlined />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setEditedPlots((items) =>
                        items.filter(
                          (item) => item.config.plotId !== plot.config.plotId,
                        ),
                      )
                    }
                    className="grid size-6 place-items-center text-editor-icon hover:text-editor-danger"
                    aria-label={`Delete ${plot.config.labels?.title || "widget"}`}
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              </div>
            ))}
            {filteredShownPlots.length === 0 && (
              <p className="text-sm italic text-editor-muted">
                {shownPlots.length
                  ? "No matching widgets"
                  : "No visible widgets"}
              </p>
            )}
          </div>
        </section>
      </div>

      <footer className="flex items-center justify-between border-t border-black/[0.06] px-3 py-3">
        <button
          type="button"
          data-testid="plot-editor-reset-button"
          onClick={() => {
            setEditedPlots(plots);
            setSearchQuery("");
          }}
          disabled={!hasChanges}
          className="h-8 rounded border border-black/25 bg-white px-2 text-sm font-medium text-brand-dark hover:bg-gray-50 disabled:cursor-default disabled:opacity-40"
        >
          Reset all
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="plot-editor-apply-button"
            onClick={applyChanges}
            disabled={!hasChanges}
            className="h-8 rounded bg-brand-primary px-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-default disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      </footer>
    </div>
  );
};
